import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Tag, Space, Form, Modal, Input, message, Spin, Upload, Popconfirm, Empty, Table, Select, Pagination } from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, 
  ArrowLeftOutlined, SearchOutlined, KeyOutlined, FolderOpenOutlined, VideoCameraOutlined,
  BarChartOutlined, DownloadOutlined, FilterOutlined
} from '@ant-design/icons';
import {
  fetchAvailableLogins
} from '../../../../Service/Courses_API/coursesApi';
import { SERVER_URL } from '../../../../Service/apiConfig';
import {
  fetchModules, createModule, updateModule, deleteModule,
  fetchModuleAccess, grantModuleAccess, revokeModuleAccess,
  uploadModuleImage
} from '../../../../Service/Modules_API/modulesApi';
import {
  fetchCourseWatchAnalytics, downloadCourseWatchAnalyticsCsv
} from '../../../../Service/VideoWatchTrack_API/videoWatchTrackApi';
import { fetchClusters } from '../../../../Service/Clusters_API/clustersApi';
import { fetchAcademicYears } from '../../../../Service/Dashboard_API/dashboardApi';
import VideoIndexPage from '../../VideoIndex/VideoIndexPage';
import CourseWatchChart from './CourseWatchChart';

const ModulesPage = ({ course, userRole, onBack }) => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(() => {
    const saved = localStorage.getItem('selectedModule');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    if (selectedModule) {
      localStorage.setItem('selectedModule', JSON.stringify(selectedModule));
    } else {
      localStorage.removeItem('selectedModule');
      localStorage.removeItem('selectedVideo');
    }
  }, [selectedModule]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  // Dropdown options lookup
  const [clusters, setClusters] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);

  // Create/Edit form states
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [moduleForm] = Form.useForm();
  
  // Image uploader states
  const [moduleImgFile, setModuleImgFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [moduleImgLoading, setModuleImgLoading] = useState(false);

  // Access control states
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [currentAccessModule, setCurrentAccessModule] = useState(null);
  const [availableLogins, setAvailableLogins] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [accessForm] = Form.useForm();
  const [editingAccessId, setEditingAccessId] = useState(null);
  const [editingAccessType, setEditingAccessType] = useState(null);
  const [accessSearchText, setAccessSearchText] = useState('');

  // Helper to format date as YYYY-MM-DD
  const formatDateForInput = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultDates = () => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(today.getDate() - 30);
    return {
      start: formatDateForInput(oneMonthAgo),
      end: formatDateForInput(today)
    };
  };

  // Analytics & CSV Export states
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => getDefaultDates().start);
  const [endDate, setEndDate] = useState(() => getDefaultDates().end);
  const [userSearchText, setUserSearchText] = useState('');

  const loadAnalyticsData = async (overrideStart, overrideEnd) => {
    if (!course?.courses_id) return;
    setAnalyticsLoading(true);
    try {
      const sDate = overrideStart !== undefined ? overrideStart : startDate;
      const eDate = overrideEnd !== undefined ? overrideEnd : endDate;
      const data = await fetchCourseWatchAnalytics(course.courses_id, sDate, eDate);
      setAnalyticsData(data);
    } catch (e) {
      message.error('Failed to load course watch analytics.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (!course?.courses_id) return;
    setExportLoading(true);
    try {
      await downloadCourseWatchAnalyticsCsv(course.courses_id, startDate, endDate);
      message.success('Analytics CSV report downloaded successfully.');
    } catch (e) {
      message.error('Failed to download CSV export.');
    } finally {
      setExportLoading(false);
    }
  };

  const loadModulesList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText && searchText.trim()) params.search = searchText.trim();
      const data = await fetchModules(course.courses_id, params);
      setModules(data);
      
      const clusterList = await fetchClusters();
      setClusters(clusterList);
      
      const yearsList = await fetchAcademicYears();
      setAcademicYears(yearsList);
    } catch (e) {
      console.warn('Failed to load modules list:', e.message);
      message.error('Failed to load modules.');
    } finally {
      setLoading(false);
    }
  }, [course.courses_id, searchText]);

  useEffect(() => {
    loadModulesList();
  }, [loadModulesList]);

  const handleSaveModule = async (values) => {
    try {
      let finalImgName = '';
      if (fileList && fileList.length > 0) {
        if (moduleImgFile) {
          setModuleImgLoading(true);
          const formData = new FormData();
          formData.append('image', moduleImgFile);
          const uploadRes = await uploadModuleImage(formData);
          finalImgName = uploadRes.filename;
        } else if (editingItem && editingItem.modules_image) {
          finalImgName = editingItem.modules_image;
        }
      } else {
        finalImgName = '';
      }

      const payload = {
        modules_name: values.modules_name,
        modules_desripyion: values.modules_desripyion || '',
        modules_image: finalImgName,
        cluster_id: values.cluster_id || null,
        academic_id: values.academic_id || null,
        courses_id: course.courses_id
      };

      if (editingItem) {
        await updateModule(editingItem.modules_id, payload);
        message.success('Module updated successfully.');
      } else {
        await createModule(course.courses_id, payload);
        message.success('Module created successfully.');
      }

      setIsModuleModalOpen(false);
      moduleForm.resetFields();
      setFileList([]);
      setModuleImgFile(null);
      setEditingItem(null);
      loadModulesList();
    } catch (e) {
      message.error('Failed to save module.');
    } finally {
      setModuleImgLoading(false);
    }
  };

  const handleDeleteModule = async (moduleId, e) => {
    if (e) e.stopPropagation();
    try {
      await deleteModule(moduleId);
      message.success('Module deleted.');
      loadModulesList();
    } catch (e) {
      message.error('Failed to delete module.');
    }
  };

  const handleManageAccess = async (moduleRecord) => {
    setCurrentAccessModule(moduleRecord);
    setIsAccessModalOpen(true);
    setAccessLoading(true);
    try {
      const list = await fetchModuleAccess(moduleRecord.modules_id);
      setAccessList(list);
      
      const logins = await fetchAvailableLogins();
      setAvailableLogins(logins);
    } catch (e) {
      message.error('Failed to load module access list.');
    } finally {
      setAccessLoading(false);
    }
  };

  const handleGrantAccess = async (values) => {
    setGrantLoading(true);
    try {
      await grantModuleAccess(currentAccessModule.modules_id, {
        login_ids: values.login_ids,
        modules_access: values.modules_access,
        courses_id: course.courses_id
      });
      message.success('Access override granted.');
      accessForm.resetFields(['login_ids', 'modules_access']);
      
      const list = await fetchModuleAccess(currentAccessModule.modules_id);
      setAccessList(list);
    } catch (e) {
      message.error(e.message || 'Failed to grant access override.');
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeAccess = async (accessId) => {
    try {
      await revokeModuleAccess(accessId);
      message.success('Access override revoked.');
      const list = await fetchModuleAccess(currentAccessModule.modules_id);
      setAccessList(list);
    } catch (e) {
      message.error('Failed to revoke access override.');
    }
  };

  // Filter modules locally based on name/description search
  const filteredModules = modules.filter(m => {
    if ((userRole === 'STUDIO' || userRole === 'USER') && m.modules_access === 'LIVE') {
      return false;
    }
    const term = searchText.toLowerCase();
    const nameMatch = (m.modules_name || '').toLowerCase().includes(term);
    const descMatch = (m.modules_desripyion || '').toLowerCase().includes(term);
    return nameMatch || descMatch;
  });

  if (selectedModule) {
    if (window.location.pathname !== '/module-videos' && window.location.pathname !== '/video-player' && window.location.pathname !== '/video-edit') {
      window.history.pushState({}, '', `/module-videos?moduleId=${selectedModule.modules_id}`);
    }
    return (
      <VideoIndexPage
        course={course}
        module={selectedModule}
        userRole={userRole}
        onBack={() => {
          setSelectedModule(null);
          window.history.pushState({}, '', `/modules?courseId=${course?.courses_id || ''}`);
        }}
      />
    );
  }

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      <style>{`
        .module-card-adaptive {
          background: #ffffff !important;
          border: 1px solid var(--border-color, #e2e8f0) !important;
          border-radius: 16px;
          overflow: hidden;
          min-height: 360px;
          height: auto;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .module-card-adaptive:hover {
          transform: translateY(-4px);
          border-color: rgba(59, 130, 246, 0.4) !important;
          box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.1) !important;
        }
        [data-theme='dark'] .module-card-adaptive {
          background: linear-gradient(145deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8)) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }
        [data-theme='dark'] .module-card-adaptive:hover {
          box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.5) !important;
        }
      `}</style>
      
      {/* Header controls section */}
      <div style={{ marginBottom: 24 }}>
        <Button style={{ marginBottom: 16 }} icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back to Courses
        </Button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Modules</h2>
            {course?.courses_name && (
              <Tag color="blue" style={{ marginTop: 6, borderRadius: 6, padding: '2px 10px', fontSize: 13, fontWeight: 500 }}>
                Course: {course.courses_name}
              </Tag>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Input
              placeholder="Search modules..."
              prefix={<SearchOutlined style={{ color: 'var(--text-secondary)' }} />}
              value={searchText}
              onChange={e => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: 220,
                background: 'var(--panel-color, #ffffff)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-color)',
                borderRadius: 8
              }}
              allowClear
            />
            <Button
              icon={<BarChartOutlined />}
              onClick={() => {
                setIsAnalyticsOpen(true);
                loadAnalyticsData();
              }}
              style={{ borderRadius: 6, borderColor: '#6366f1', color: '#818cf8' }}
            >
              Analytics & Export
            </Button>
            {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => { 
                  setEditingItem(null); 
                  setFileList([]);
                  setModuleImgFile(null);
                  moduleForm.resetFields();
                  setIsModuleModalOpen(true); 
                }}
              >
                Add Module
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Grid content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><Spin size="large" /></div>
      ) : filteredModules.length > 0 ? (
        <>
          <Row gutter={[16, 16]}>
            {filteredModules
              .filter((m) => (m.modules_access || m.default_access) !== 'BLOCK')
              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
              .map((m) => {
                const cluster = clusters.find(cl => cl.cluster_id === m.cluster_id);
                const academicYear = academicYears.find(y => y.academic_year_id === m.academic_id);

                return (
                  <Col xs={24} sm={12} md={8} lg={8} xl={8} key={m.modules_id}>
                    <Card
                      className="module-card-adaptive"
                      hoverable
                      onClick={() => setSelectedModule(m)}
                      bodyStyle={{
                        padding: 18,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                      cover={
                        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {m.modules_image ? (
                            <img
                              src={`${SERVER_URL}/${m.modules_directory || 'Uploads'}/${m.modules_image}`}
                              alt={m.modules_name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }}>
                              <FolderOpenOutlined style={{ fontSize: 42, color: 'rgba(255,255,255,0.2)' }} />
                            </div>
                          )}

                          {/* Video Count & Duration Overlay Badge */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 10,
                              right: 12,
                              zIndex: 2,
                              background: 'rgba(0, 0, 0, 0.75)',
                              backdropFilter: 'blur(4px)',
                              padding: '3px 9px',
                              borderRadius: 6,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              fontSize: 12,
                              color: '#fff',
                              fontWeight: 500
                            }}
                          >
                            <VideoCameraOutlined style={{ color: '#38bdf8' }} />
                            <span>{m.video_count || 0} Videos • {m.total_duration || '00:00:00'}</span>
                          </div>
                        </div>
                      }
                    >
                      <div style={{ marginBottom: 12 }}>
                        <h3 style={{ color: 'var(--text-color, #1e293b)', fontSize: 16, marginBottom: 6, fontWeight: 600, wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.4, letterSpacing: '-0.01em' }}>
                          {m.modules_name}
                        </h3>
                        {m.modules_desripyion && (
                          <p style={{ color: 'var(--text-secondary, #64748b)', fontSize: 13, minHeight: 20, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 6, lineHeight: 1.5 }}>
                            {m.modules_desripyion}
                          </p>
                        )}
                        {cluster && (
                          <Tag
                            style={{
                              borderRadius: 6,
                              border: '1px solid rgba(147, 51, 234, 0.3)',
                              background: 'rgba(147, 51, 234, 0.12)',
                              color: '#9333ea',
                              fontWeight: 500,
                              margin: '4px 0 0 0',
                              padding: '2px 8px'
                            }}
                          >
                            {cluster.cluster_name} {cluster.academic_year ? `(${cluster.academic_year})` : ''}
                          </Tag>
                        )}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--border-color, rgba(0, 0, 0, 0.06))' }}>
                        <div>
                          {academicYear ? (
                            <Tag style={{ borderRadius: 6, border: '1px solid rgba(6, 182, 212, 0.3)', background: 'rgba(6, 182, 212, 0.12)', color: '#0891b2', margin: 0, padding: '2px 8px' }}>
                              {academicYear.academic_year}
                            </Tag>
                          ) : (
                            <Tag style={{ borderRadius: 6, border: '1px solid rgba(16, 185, 129, 0.3)', background: 'rgba(16, 185, 129, 0.12)', color: '#059669', margin: 0, padding: '2px 8px' }}>
                              {m.modules_access || 'LIVE/VOD'}
                            </Tag>
                          )}
                        </div>

                        {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                          <Space size={6}>
                            <Button
                              icon={<EditOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingItem(m);
                                moduleForm.setFieldsValue(m);
                                setFileList(m.modules_image ? [{
                                  uid: '-1',
                                  name: m.modules_image,
                                  status: 'done',
                                  url: `${SERVER_URL}/${m.modules_directory || 'Uploads'}/${m.modules_image}`
                                }] : []);
                                setModuleImgFile(null);
                                setIsModuleModalOpen(true);
                              }}
                              style={{ borderRadius: 6, display: 'flex', alignItems: 'center', background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)', color: '#818cf8' }}
                            />
                            <Button
                              icon={<KeyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleManageAccess(m);
                              }}
                              style={{ borderRadius: 6, display: 'flex', alignItems: 'center', background: 'rgba(250, 173, 20, 0.1)', border: '1px solid rgba(250, 173, 20, 0.3)', color: '#fbbf24' }}
                            />
                            <Popconfirm
                              title="Delete this module?"
                              onConfirm={(e) => handleDeleteModule(m.modules_id, e)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => e.stopPropagation()}
                                style={{ borderRadius: 6, display: 'flex', alignItems: 'center' }}
                              />
                            </Popconfirm>
                          </Space>
                        )}
                      </div>
                    </Card>
                  </Col>
                );
              })}
          </Row>

          {filteredModules.length > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredModules.length}
                onChange={page => setCurrentPage(page)}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      ) : (
        <Empty description={<span style={{ color: 'var(--text-secondary)' }}>No modules found matching search query.</span>} style={{ margin: '40px auto' }} />
      )}

      {/* Module Creation/Editing Modal */}
      <Modal
        title={editingItem ? 'Edit Module' : 'Create Module'}
        open={isModuleModalOpen}
        onCancel={() => {
          setIsModuleModalOpen(false);
          setFileList([]);
          setModuleImgFile(null);
          setEditingItem(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={moduleForm} layout="vertical" onFinish={handleSaveModule}>
          <Form.Item name="modules_name" label="Module Name" rules={[{ required: true, message: 'Please enter module name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="modules_desripyion" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>


          <Form.Item label="Upload Module Image">
            <Upload
              beforeUpload={(file) => {
                setModuleImgFile(file);
                return false;
              }}
              maxCount={1}
              listType="picture"
              onRemove={() => {
                setModuleImgFile(null);
                setFileList([]);
              }}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
            >
              <Button icon={<UploadOutlined />}>Select Image</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setIsModuleModalOpen(false);
                setFileList([]);
                setModuleImgFile(null);
                setEditingItem(null);
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={moduleImgLoading}>
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Module Access Override Modal */}
      <Modal
        title={`Manage Override Access - ${currentAccessModule?.modules_name}`}
        open={isAccessModalOpen}
        onCancel={() => {
          setIsAccessModalOpen(false);
          setCurrentAccessModule(null);
          setEditingAccessId(null);
          setAccessSearchText('');
          accessForm.resetFields();
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 12 }}>Grant Access Override</h4>
          <Form form={accessForm} layout="inline" onFinish={handleGrantAccess} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
            <Form.Item name="login_ids" rules={[{ required: true, message: 'Select user(s)' }]} style={{ minWidth: 240, margin: 0 }}>
              <Select
                mode="multiple"
                placeholder="Select Studio or Student account"
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                options={availableLogins
                  .filter(l => !accessList.some(a => a.login_id === l.login_id))
                  .map(l => ({
                    value: l.login_id,
                    label: `${l.login_name} (${l.login_username} - ${l.login_role})`
                  }))}
              />
            </Form.Item>
            
            <Form.Item name="modules_access" rules={[{ required: true, message: 'Select type' }]} style={{ minWidth: 140, margin: 0 }}>
              <Select showSearch optionFilterProp="children" placeholder="Access Override">
                <Select.Option value="LIVE">LIVE</Select.Option>
                <Select.Option value="VOD">VOD</Select.Option>
                <Select.Option value="LIVE/VOD">LIVE/VOD</Select.Option>
                <Select.Option value="BLOCK">BLOCK</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item style={{ margin: 0 }}>
              <Button type="primary" htmlType="submit" loading={grantLoading}>
                Grant Override
              </Button>
            </Form.Item>
          </Form>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Current Override Access List</h4>
            <Input 
              placeholder="Search by name, username or role..." 
              value={accessSearchText}
              onChange={(e) => setAccessSearchText(e.target.value)}
              prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.25)' }} />}
              style={{ width: 280 }}
              allowClear
            />
          </div>
          <Table
            dataSource={accessList.filter(item => {
              const term = accessSearchText.toLowerCase();
              return (item.login_name || '').toLowerCase().includes(term) ||
                     (item.login_username || '').toLowerCase().includes(term) ||
                     (item.login_role || '').toLowerCase().includes(term);
            })}
            rowKey="modules_access_id"
            loading={accessLoading}
            size="small"
            pagination={{ pageSize: 20 }}
            scroll={{ x: 'max-content', y: 400 }}
            columns={[
              { title: 'Name', dataIndex: 'login_name', key: 'login_name' },
              { title: 'Username', dataIndex: 'login_username', key: 'login_username' },
              {
                title: 'Role',
                dataIndex: 'login_role',
                key: 'login_role',
                render: (role) => <Tag color={role === 'STUDIO' ? 'orange' : 'blue'}>{role}</Tag>
              },
              {
                title: 'Access Override',
                dataIndex: 'modules_access',
                key: 'modules_access',
                render: (type, record) => {
                  const isEditing = record.modules_access_id === editingAccessId;
                  if (isEditing) {
                    return (
                      <Select showSearch optionFilterProp="children"
                        value={editingAccessType}
                        size="small"
                        style={{ width: 120 }}
                        onChange={(val) => setEditingAccessType(val)}
                      >
                        <Select.Option value="LIVE">LIVE</Select.Option>
                        <Select.Option value="VOD">VOD</Select.Option>
                        <Select.Option value="LIVE/VOD">LIVE/VOD</Select.Option>
                        <Select.Option value="BLOCK">BLOCK</Select.Option>
                      </Select>
                    );
                  }
                  return <Tag color="green">{type}</Tag>;
                }
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 140,
                render: (_, record) => {
                  const isEditing = record.modules_access_id === editingAccessId;
                  if (isEditing) {
                    return (
                      <Space size={8}>
                        <Button
                          size="small"
                          type="link"
                          onClick={async () => {
                            try {
                              await grantModuleAccess(currentAccessModule.modules_id, {
                                login_id: record.login_id,
                                modules_access: editingAccessType,
                                courses_id: course.courses_id
                              });
                              message.success('Override updated successfully.');
                              setEditingAccessId(null);
                              const list = await fetchModuleAccess(currentAccessModule.modules_id);
                              setAccessList(list);
                            } catch (e) {
                              message.error('Failed to update override.');
                            }
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() => setEditingAccessId(null)}
                        >
                          Cancel
                        </Button>
                      </Space>
                    );
                  }
                  return (
                    <Space size={8}>
                      <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingAccessId(record.modules_access_id);
                          setEditingAccessType(record.modules_access);
                        }}
                      />
                      <Popconfirm
                        title="Revoke access override for this user?"
                        onConfirm={() => handleRevokeAccess(record.modules_access_id)}
                      >
                        <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  );
                }
              }
            ]}
          />
        </div>
      </Modal>

      {/* Course Watch Analytics & CSV Export Modal */}
      <Modal
        title={`Course Watch Analytics - ${course?.courses_name}`}
        open={isAnalyticsOpen}
        onCancel={() => setIsAnalyticsOpen(false)}
        footer={null}
        width={1240}
        style={{ top: 20, maxWidth: '95vw' }}
        destroyOnClose
      >
        {/* Filter & Export Toolbar */}
        <div style={{ background: 'var(--panel-color)', padding: 16, borderRadius: 8, marginBottom: 20, border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Space size="middle" wrap>
            <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px', borderRadius: 4 }}>
              Course ID: {course?.courses_id}
            </Tag>
            <Input
              placeholder="Search by user or username..."
              prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.25)' }} />}
              value={userSearchText}
              onChange={e => setUserSearchText(e.target.value)}
              allowClear
              style={{ width: 280 }}
            />
            <Space>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>From:</span>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: 140 }} />
            </Space>
            <Space>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>To:</span>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: 140 }} />
            </Space>
            <Button icon={<FilterOutlined />} type="default" onClick={() => loadAnalyticsData(startDate, endDate)} loading={analyticsLoading}>
              Apply Filter
            </Button>
            <Button type="link" onClick={() => {
              const def = getDefaultDates();
              setStartDate(def.start);
              setEndDate(def.end);
              setUserSearchText('');
              loadAnalyticsData(def.start, def.end);
            }} style={{ padding: 0 }}>
              Reset
            </Button>
          </Space>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportCsv} loading={exportLoading}>
            Download CSV
          </Button>
        </div>

        {/* Dynamic calculation for filtered analytics */}
        {(() => {
          const rawRecords = analyticsData?.records || [];
          const filteredRecords = rawRecords.filter(rec => {
            if (!userSearchText || !userSearchText.trim()) return true;
            const q = userSearchText.toLowerCase().trim();
            const nameStr = (rec.user_name || '').toLowerCase();
            const usernameStr = (rec.login_username || '').toLowerCase();
            const idStr = String(rec.login_id || '');
            const videoTitleStr = (rec.video_title || '').toLowerCase();
            const moduleNameStr = (rec.modules_name || '').toLowerCase();
            return nameStr.includes(q) || usernameStr.includes(q) || idStr.includes(q) || videoTitleStr.includes(q) || moduleNameStr.includes(q);
          });

          let totalSecs = 0;
          const filteredUserIds = new Set();
          filteredRecords.forEach(r => {
            if (r.login_id) filteredUserIds.add(r.login_id);
            if (r.video_duration) {
              const parts = String(r.video_duration).split(':');
              if (parts.length === 3) {
                totalSecs += parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
              }
            }
          });

          const formatSecsToTime = (sec) => {
            const h = String(Math.floor(sec / 3600)).padStart(2, '0');
            const m = String(Math.floor((sec % 3600) / 60)).padStart(2, '0');
            const s = String(Math.floor(sec % 60)).padStart(2, '0');
            return `${h}:${m}:${s}`;
          };

          return (
            <div style={{ marginTop: 20 }}>
              {/* Summary Stats Cards */}
              {analyticsData?.summary && (
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                  <Col xs={24} sm={8}>
                    <Card size="small" style={{ background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: 8 }}>
                      <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Total Watch Sessions</div>
                      <div style={{ fontSize: 22, fontWeight: 'bold', color: '#818cf8', marginTop: 4 }}>
                        {filteredRecords.length}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card size="small" style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: 8 }}>
                      <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Total Time Watched</div>
                      <div style={{ fontSize: 22, fontWeight: 'bold', color: '#10b981', marginTop: 4 }}>
                        {formatSecsToTime(totalSecs)}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card size="small" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: 8 }}>
                      <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Unique Viewers</div>
                      <div style={{ fontSize: 22, fontWeight: 'bold', color: '#f59e0b', marginTop: 4 }}>
                        {filteredUserIds.size}
                      </div>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Detailed Watch Log Table */}
              <Table
                dataSource={filteredRecords}
                loading={analyticsLoading}
                rowKey="video_watch_track_id"
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 30,
                  defaultPageSize: 30,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '30', '50', '100'],
                  showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} records`
                }}
                columns={[
                  {
                    title: 'User',
                    dataIndex: 'user_name',
                    key: 'user_name',
                    render: (name, rec) => (
                      <div>
                        <strong>{name}</strong>
                        {rec.login_username && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>@{rec.login_username}</div>}
                      </div>
                    )
                  },
                  { title: 'Module', dataIndex: 'modules_name', key: 'modules_name', render: text => text || 'N/A' },
                  { title: 'Video Title', dataIndex: 'video_title', key: 'video_title', render: text => <strong>{text}</strong> },
                  { title: 'Type', dataIndex: 'video_type', key: 'video_type', render: type => <Tag color={type === 'LIVE' ? 'red' : 'blue'}>{type || 'VOD'}</Tag> },
                  { title: 'Watched Duration', dataIndex: 'video_duration', key: 'video_duration', render: dur => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#10b981' }}>{dur || '00:00:00'}</span> },
                  { title: 'Start Time', dataIndex: 'start_time', key: 'start_time', render: dateStr => dateStr ? new Date(dateStr).toLocaleString() : 'N/A' },
                  { title: 'IP Address', dataIndex: 'ip_addess', key: 'ip_addess', render: ip => <Tag color="default">{ip || 'N/A'}</Tag> }
                ]}
              />
            </div>
          );
        })()}
      </Modal>

    </div>
  );
};

export default ModulesPage;
